import client from "FrontendJsAssets/common/client";
import { Vue } from 'Common/vue'
import Vuex from 'vuex'
import { CancelToken } from "axios";

Vue.use(Vuex)

const endpoints = {
    initialize: '/init-product-reservation',
    submit: '/submit-product-reservation',
    updateQuantity: '/update-reservation-quantity',
    addVoucher: '/add-reservation-voucher',
    removeVoucher: '/remove-reservation-voucher',
}

const branchReservationEl = document.querySelector('.vue-branch-reservation')
let product = {}
let branches = []
let selectedBranchId = null
if (branchReservationEl) {
    product = JSON.parse(branchReservationEl.dataset.product) || {}
    branches = JSON.parse(branchReservationEl.dataset.branchQuantities) || []

    if (branches.length) {
        branches = branches.filter(branch => branch.stock > 0)
    }

    if (branches.length === 1) {
        selectedBranchId = branches[0].id
    } else if (window.sessionStorage.getItem(`product_reservation_branch_${product.id}`)) {
        selectedBranchId = window.sessionStorage.getItem(`product_reservation_branch_${product.id}`)
    }
}

const lang = document.querySelector('html').getAttribute('lang')
let branchReservationLocale
import(`Locale/${lang}`).then((module) => {
    window.generalErrorMessage = module.generalError
    branchReservationLocale = module.BranchReservation
})

const catchCallback = (error) => {
    let errorMessage

    if (error.customError && error.message) {
        errorMessage = error.message
    } else {
        errorMessage = window.generalErrorMessage
    }

    return {
        error: true,
        message: errorMessage
    }
}

const handleResponseStatus = (response) => {
    if (response.status !== 200 || !response.data || !response.data.status) {
        throw {
            customError: true,
            message: response.data ? response.data.message : window.generalErrorMessage
        }
    }
}

let updateQuantityCancelToken;

const BranchReservationStore = new Vuex.Store({
    state: {
        isInitializing: false,
        isInitialized: false,
        product: product,
        branches: branches,
        selectedBranchId: parseInt(selectedBranchId),
        selectedQuantity: 1,
        isUpdatingQuantity: false,
        isUpdatingVoucher: false,
        isSubmittingForm: false,
        totals: null,
        vouchers: [],
        orderId: null
    },
    getters: {
        selectedBranch(state) {
            return state.branches.find(branch => branch.id === state.selectedBranchId) || false
        },
        orderId(state) {
            return state.orderId
        },
        isUpdating(state) {
            return (
                state.isUpdatingQuantity ||
                state.isUpdatingVoucher ||
                state.isSubmittingForm
            )
        },
        isSubmitDisabled(state, getters) {
            return getters.isUpdating || !state.selectedBranchId || !state.isInitialized
        }
    },
    actions: {
        async initReservation({ state, commit }) {
            if (state.isInitializing || state.isInitialized) {
                return false
            }

            state.isInitializing = true

            try {
                const response = await client.post(endpoints.initialize, {
                    product_id: product.id
                })

                handleResponseStatus(response)

                commit('setTotals', response.data.totals)
                commit('setVouchers', response.data.vouchers)
                commit('updateQuantity', response.data.quantity)

                state.isInitializing = false
                state.isInitialized = true
            } catch (error) {
                if (client.isCancel(error)) {
                    return
                }

                state.isInitializing = false
                return catchCallback(error)
            }
        },
        async submitForm({ state }, formData) {
            try {
                if (state.isSubmitDisabled) throw `Submitting is disabled`

                state.isSubmittingForm = true

                const response = await client.post(endpoints.submit, {
                    formData: formData
                })

                handleResponseStatus(response)

                state.isSubmittingForm = false
                return response
            } catch (error) {
                if (client.isCancel(error)) {
                    return
                }

                state.isSubmittingForm = false
                return catchCallback(error)
            }
        },
        async submitQuantity({ state, commit }, quantity) {
            commit('updateQuantity', quantity)
            state.isUpdatingQuantity = true

            if (updateQuantityCancelToken) {
                updateQuantityCancelToken.cancel()
            }

            updateQuantityCancelToken = CancelToken.source()

            try {
                const response = await client.post(endpoints.updateQuantity, {
                    quantity: quantity
                }, {
                    cancelToken: updateQuantityCancelToken.token
                })

                handleResponseStatus(response)

                commit('setTotals', response.data.totals)
                state.isUpdatingQuantity = false
            } catch (error) {
                if (client.isCancel(error)) {
                    return
                }

                state.isUpdatingQuantity = false
                return catchCallback(error)
            }
        },
        async addVoucher({ commit, state }, voucherCode) {
            if (state.isUpdatingVoucher) {
                return false
            }

            commit('setUpdatingVoucher', true)
            try {
                const response = await client.post(endpoints.addVoucher, {
                    voucher_code: voucherCode
                })

                handleResponseStatus(response)

                commit('setTotals', response.data.totals)
                commit('setVouchers', response.data.vouchers)
                commit('setUpdatingVoucher', false)

                return response
            } catch (error) {
                commit('setUpdatingVoucher', false)
                return catchCallback(error)
            }
        },
        async removeVoucher({ commit, state }, voucherCartId) {
            if (state.isUpdatingVoucher) {
                return false
            }

            commit('setUpdatingVoucher', true)
            try {
                const response = await client.post(endpoints.removeVoucher, {
                    voucher_id: voucherCartId
                })

                handleResponseStatus(response)

                commit('setTotals', response.data.totals)
                commit('setVouchers', response.data.vouchers)
                commit('setUpdatingVoucher', false)

                return response
            } catch (error) {
                commit('setUpdatingVoucher', false)
                return catchCallback(error)
            }
        },
        async setOrderId({ commit, state }, orderId) {
            commit('setOrderId', orderId)
        }
    },
    mutations: {
        selectBranch(state, branchId) {
            state.selectedBranchId = branchId

            window.sessionStorage.setItem(`product_reservation_branch_${product.id}`, branchId)
        },
        updateQuantity(state, quantity) {
            state.selectedQuantity = quantity
        },
        setUpdatingVoucher(state, value) {
            state.isUpdatingVoucher = value
        },
        setOrderId(state, orderId) {
            state.orderId = orderId
        },
        setTotals(state, totals) {
            state.totals = totals
        },
        setVouchers(state, vouchers) {
            state.vouchers = Object.values(vouchers)
        }
    }
})

export default BranchReservationStore
